import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Wallet, Clock, CheckCircle, Link2, Building2, Pencil, Receipt } from "lucide-react";
import { AccountDetailsType } from "../../../../Data/package.dto";
import { useAppDispatch, useAppSelector } from "../../../../Redux/Hooks";
import { getAccountInfoAPI, getCashfreeVendorStatusAPI } from "../../../../Redux/ApiCalls/Dashboard/PackageAPI";
import AddAccountInformationModal from "../../../atoms/Dashboard/Package/AddAccountInformationModal";
import ManageUpiModal from "../../../atoms/Dashboard/CustomerTransaction/ManageUpiModal";
import PaymentLinkModal from "../../../atoms/Dashboard/CustomerTransaction/PaymentLinkModal";
import PaymentLinkTypeModal from "../../../atoms/Dashboard/CustomerTransaction/PaymentLinkTypeModal";
import ManualPaymentTypeModal from "../../../atoms/Dashboard/CustomerTransaction/ManualPaymentTypeModal";
import NewCustomerManualPaymentModal from "../../../atoms/Dashboard/CustomerTransaction/NewCustomerManualPaymentModal";
import AlbumPaymentModal from "../../../atoms/Dashboard/CustomerTransaction/AlbumPaymentModal";
import TransactionHistory from "../../../atoms/Dashboard/CustomerTransaction/TransationList";
import { showErrorToast } from "../../../atoms/Utlis/Toast";
import {
  getCustomerTransactionAPI,
  getStudioUpiVpaAPI,
} from "../../../../Redux/ApiCalls/Dashboard/CustomerTransationApi";
import { StudioUpiDetails } from "../../../../Data/transaction.dto";
import { device } from "../../../atoms/Utlis/media";

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(value);
}

const CustomerTransaction = () => {
  const [userAccount, setUserAccount] = useState<AccountDetailsType>();
  const [showTypeSelectionModal, setShowTypeSelectionModal] = useState(false);
  const [showManualTypeSelectionModal, setShowManualTypeSelectionModal] = useState(false);
  const [showCreatePaymentModal, setShowPaymentModal] = useState(false);
  const [showNewCustomerManualModal, setShowNewCustomerManualModal] = useState(false);
  const [showAlbumPaymentModal, setShowAlbumPaymentModal] = useState(false);
  const [albumModalMode, setAlbumModalMode] = useState<"link" | "manual">("link");
  const [paymentType, setPaymentType] = useState<"general" | "album">("general");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const pageSize = 10;
  const { account, isAccountUpdated, cashfreeVendorStatus } = useAppSelector(
    (state) => state.package
  );
  const { transactions, loading, customer_payments, count, total_pages } = useAppSelector(
    (state) => state.customer_transaction
  );
  const [editAccountPopup, setEditAccountPopup] = useState<boolean>(false);
  const [studioUpi, setStudioUpi] = useState<StudioUpiDetails | null>(null);
  const [showUpiModal, setShowUpiModal] = useState<boolean>(false);

  const dispatch = useAppDispatch();

  const fetchStudioUpi = async () => {
    try {
      const res = await getStudioUpiVpaAPI();
      if (res && res.data) {
        setStudioUpi(res.data);
      }
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    fetchStudioUpi();
  }, []);

  useEffect(() => {
    dispatch(
      getCustomerTransactionAPI({
        page: currentPage,
        page_size: pageSize,
        ...(paymentType === "album" ? { album_only: true } : { general_only: true }),
      })
    );
  }, [dispatch, currentPage, paymentType]);

  useEffect(() => {
    if (isAccountUpdated) {
      dispatch(getAccountInfoAPI());
      dispatch(getCashfreeVendorStatusAPI());
    }
  }, [dispatch, isAccountUpdated]);

  useEffect(() => {
    if (account) {
      setUserAccount(account);
    }
  }, [account]);

  return (
    <PageInner>
      <Toolbar>
        <ToolbarText>
          <SectionTitle>Payment links & transactions</SectionTitle>
          <SectionHint>Create links and track incoming customer payments</SectionHint>
        </ToolbarText>
        <ButtonGroup>
          <ManualButton
            type="button"
            onClick={() => setShowManualTypeSelectionModal(true)}
          >
            <Receipt size={16} strokeWidth={2.4} />
            Manual Payment
          </ManualButton>
          <CreateButton
            type="button"
            onClick={() => {
              setShowTypeSelectionModal(true);
            }}
          >
            <Link2 size={16} strokeWidth={2.4} />
            Create Payment Link
          </CreateButton>
        </ButtonGroup>
      </Toolbar>

      {customer_payments && (
        <StatsGrid>
          <WalletHero>
            <WalletIconWrap>
              <Wallet size={16} />
            </WalletIconWrap>
            <WalletHeroBody>
              <WalletHeroValue>{formatCurrency(customer_payments.total_payments)}</WalletHeroValue>
              <WalletHeroLabel>Total Payments</WalletHeroLabel>
            </WalletHeroBody>
          </WalletHero>

          <StatCard $tone="#f2ad6d">
            <StatIcon $tone="#f2ad6d">
              <Clock size={16} />
            </StatIcon>
            <StatContent>
              <StatValue>{formatCurrency(customer_payments.payments_pending)}</StatValue>
              <StatLabel>Pending</StatLabel>
            </StatContent>
          </StatCard>

          <StatCard $tone="#31a66a">
            <StatIcon $tone="#31a66a">
              <CheckCircle size={16} />
            </StatIcon>
            <StatContent>
              <StatValue>{formatCurrency(customer_payments.payments_received)}</StatValue>
              <StatLabel>Received</StatLabel>
            </StatContent>
          </StatCard>
        </StatsGrid>
      )}

      <BankCard>
        <BankCardHeader>
          <BankCardTitleRow>
            <BankIconWrap>
              <Building2 size={18} />
            </BankIconWrap>
            <BankCardTitle>Bank account</BankCardTitle>
          </BankCardTitleRow>
          <EditButton type="button" onClick={() => setEditAccountPopup(true)} aria-label="Edit bank details">
            <Pencil size={15} />
          </EditButton>
        </BankCardHeader>

        <BankGrid>
          <BankField>
            <InfoLabel>Account name</InfoLabel>
            <InfoValue>{userAccount?.beneficiary_name ?? "—"}</InfoValue>
          </BankField>
          <BankField>
            <InfoLabel>Account number</InfoLabel>
            <InfoValue>{userAccount?.account_number ?? "—"}</InfoValue>
          </BankField>
          <BankField>
            <InfoLabel>IFSC code</InfoLabel>
            <InfoValue>{userAccount?.ifsc_code ?? "—"}</InfoValue>
          </BankField>
          <BankField>
            <InfoLabel>Vendor status</InfoLabel>
            {cashfreeVendorStatus?.cashfree_vendor_status ? (
              <StatusBadge $status={cashfreeVendorStatus.cashfree_vendor_status}>
                {String(cashfreeVendorStatus.cashfree_vendor_status).toUpperCase()}
              </StatusBadge>
            ) : (
              <InfoValue>—</InfoValue>
            )}
          </BankField>
          <BankField>
            <InfoLabel>UPI ID</InfoLabel>
            <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
              <InfoValue style={studioUpi?.has_upi_vpa ? undefined : { color: "rgba(23, 23, 23, 0.45)" }}>
                {studioUpi?.has_upi_vpa
                  ? studioUpi.upi_vpa
                  : "Not set"}
              </InfoValue>
              <button
                type="button"
                onClick={() => setShowUpiModal(true)}
                style={{
                  background: "none",
                  border: "none",
                  color: "#c94daa",
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: "pointer",
                  padding: "2px 4px",
                  textDecoration: "underline",
                }}
              >
                {studioUpi?.has_upi_vpa ? "Edit" : "Set UPI"}
              </button>
            </div>
          </BankField>
        </BankGrid>

        {cashfreeVendorStatus?.remarks && (
          <RemarksRow>
            <InfoLabel>Remarks</InfoLabel>
            <RemarksText>{cashfreeVendorStatus.remarks}</RemarksText>
          </RemarksRow>
        )}
      </BankCard>

      {editAccountPopup && (
        <AddAccountInformationModal
          account_number={userAccount?.account_number}
          ifsc_code={userAccount?.ifsc_code}
          beneficiary_name={userAccount?.beneficiary_name}
          pan={userAccount?.pan}
          uidai={userAccount?.uidai}
          tnc_accepted={userAccount?.tnc_accepted}
          address_line_1={userAccount?.address_line_1}
          address_line_2={userAccount?.address_line_2}
          city={userAccount?.city}
          state={userAccount?.state}
          zip_code={userAccount?.zip_code}
          country={userAccount?.country}
          user_address={userAccount?.user_address}
          onClose={() => setEditAccountPopup(false)}
        />
      )}

      {showUpiModal && (
        <ManageUpiModal
          isOpen={showUpiModal}
          onClose={() => setShowUpiModal(false)}
          onSuccess={fetchStudioUpi}
          initialVpa={studioUpi?.upi_vpa}
          initialPayeeName={studioUpi?.upi_payee_name}
        />
      )}

      {showTypeSelectionModal && (
        <PaymentLinkTypeModal
          isOpen={showTypeSelectionModal}
          onClose={() => setShowTypeSelectionModal(false)}
          onSelectGeneral={() => setShowPaymentModal(true)}
          onSelectAlbum={() => {
            setAlbumModalMode("link");
            setShowAlbumPaymentModal(true);
          }}
        />
      )}

      {showManualTypeSelectionModal && (
        <ManualPaymentTypeModal
          isOpen={showManualTypeSelectionModal}
          onClose={() => setShowManualTypeSelectionModal(false)}
          onSelectGeneral={() => setShowNewCustomerManualModal(true)}
          onSelectAlbum={() => {
            setAlbumModalMode("manual");
            setShowAlbumPaymentModal(true);
          }}
        />
      )}

      {showCreatePaymentModal && (
        <PaymentLinkModal
          isOpen={showCreatePaymentModal}
          onClose={() => setShowPaymentModal(false)}
          onSuccess={() => {
            dispatch(
              getCustomerTransactionAPI({
                page: currentPage,
                page_size: pageSize,
                ...(paymentType === "album" ? { album_only: true } : { general_only: true }),
              })
            );
          }}
        />
      )}

      {showNewCustomerManualModal && (
        <NewCustomerManualPaymentModal
          isOpen={showNewCustomerManualModal}
          onClose={() => setShowNewCustomerManualModal(false)}
          onSuccess={() => {
            dispatch(
              getCustomerTransactionAPI({
                page: currentPage,
                page_size: pageSize,
                ...(paymentType === "album" ? { album_only: true } : { general_only: true }),
              })
            );
          }}
        />
      )}

      {showAlbumPaymentModal && (
        <AlbumPaymentModal
          isOpen={showAlbumPaymentModal}
          mode={albumModalMode}
          onClose={() => setShowAlbumPaymentModal(false)}
          onSuccess={() => {
            dispatch(
              getCustomerTransactionAPI({
                page: currentPage,
                page_size: pageSize,
                ...(paymentType === "album" ? { album_only: true } : { general_only: true }),
              })
            );
          }}
        />
      )}

      <TransactionsSection>
        <FilterBar>
          <FilterPill
            $active={paymentType === "general"}
            type="button"
            onClick={() => {
              setPaymentType("general");
              setCurrentPage(1);
            }}
          >
            New Customer Payments
          </FilterPill>
          <FilterPill
            $active={paymentType === "album"}
            type="button"
            onClick={() => {
              setPaymentType("album");
              setCurrentPage(1);
            }}
          >
            Existing Customer Payments
          </FilterPill>
        </FilterBar>

        <TransactionHistory
          transactions={transactions}
          loading={loading}
          count={count}
          currentPage={currentPage}
          totalPages={total_pages}
          onPageChange={(page) => setCurrentPage(page)}
          paymentType={paymentType}
        />
      </TransactionsSection>
    </PageInner>
  );
};

export default CustomerTransaction;

const gradient = "linear-gradient(144.01deg, rgba(255, 201, 81, 0.8) 0%, rgba(206, 86, 176, 0.8) 100%)";

const PageInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 22px 24px;
  box-sizing: border-box;
  @media ${device.tablet} { padding: 16px; }
`;

const Toolbar = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

const ToolbarText = styled.div`
  min-width: 0;
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-family: Urbanist, sans-serif;
  font-size: 18px;
  font-weight: 800;
  color: #171717;
`;

const SectionHint = styled.p`
  margin: 4px 0 0;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.55);
`;

const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const ManualButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 42px;
  padding: 0 18px;
  border: none;
  border-radius: 12px;
  background: ${gradient};
  color: #fff;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 8px 18px rgba(201, 77, 170, 0.22);
  white-space: nowrap;
  transition: transform 0.15s ease;
  &:hover { transform: translateY(-1px); }
`;

const CreateButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 42px;
  padding: 0 18px;
  border: none;
  border-radius: 12px;
  background: ${gradient};
  color: #fff;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 8px 18px rgba(201, 77, 170, 0.22);
  white-space: nowrap;
  transition: transform 0.15s ease;
  &:hover { transform: translateY(-1px); }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  @media ${device.laptop} { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const WalletHero = styled.div`
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 16px;
  color: #fff;
  background: ${gradient};
  box-shadow: 0 8px 22px rgba(201, 77, 170, 0.22);
  text-align: left;
  &::after {
    content: "";
    position: absolute;
    right: -20px;
    bottom: -24px;
    width: 90px;
    height: 90px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.12);
  }
`;

const WalletIconWrap = styled.div`
  position: relative;
  z-index: 1;
  width: 36px;
  height: 36px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.22);
`;

const WalletHeroBody = styled.div`
  position: relative;
  z-index: 1;
  min-width: 0;
`;

const WalletHeroValue = styled.div`
  font-family: Urbanist, sans-serif;
  font-size: 20px;
  font-weight: 800;
  color: #fff;
  line-height: 1.1;
`;

const WalletHeroLabel = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.88);
  margin-top: 2px;
`;

const StatCard = styled.div<{ $tone: string }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 16px;
  background: #fff;
  border: 1px solid rgba(23, 23, 23, 0.06);
  box-shadow: 0 6px 18px rgba(23, 23, 23, 0.04);
`;

const StatIcon = styled.div<{ $tone: string }>`
  width: 36px;
  height: 36px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${(p) => p.$tone};
  background: ${(p) => `${p.$tone}18`};
`;

const StatContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const StatLabel = styled.span`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.5);
  margin-top: 2px;
`;

const StatValue = styled.span`
  font-family: Urbanist, sans-serif;
  font-size: 20px;
  font-weight: 800;
  color: #171717;
  line-height: 1.1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const BankCard = styled.div`
  background: #fff;
  border-radius: 18px;
  padding: 20px 22px;
  border: 1px solid rgba(23, 23, 23, 0.06);
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.04);
`;

const BankCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
`;

const BankCardTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const BankIconWrap = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(201, 77, 170, 0.1);
  color: #c94daa;
`;

const BankCardTitle = styled.h3`
  margin: 0;
  font-family: Urbanist, sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #171717;
`;

const EditButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: none;
  background: ${gradient};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(201, 77, 170, 0.25);
  transition: transform 0.15s ease;
  &:hover { transform: translateY(-1px); }
`;

const BankGrid = styled.div`
  display: grid;
  grid-template-columns:repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px 24px;
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const BankField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const InfoLabel = styled.span`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: rgba(23, 23, 23, 0.45);
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const InfoValue = styled.span`
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #171717;
`;

const StatusBadge = styled.span<{ $status: string }>`
  display: inline-flex;
  width: fit-content;
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 999px;
  background-color: ${(props) => {
    const s = props.$status.toLowerCase();
    if (s.includes("active") || s.includes("success")) return "rgba(49,166,106,0.12)";
    if (s.includes("pending")) return "rgba(242,173,109,0.15)";
    if (s.includes("reject") || s.includes("fail")) return "rgba(228,72,98,0.12)";
    return "rgba(23,23,23,0.08)";
  }};
  color: ${(props) => {
    const s = props.$status.toLowerCase();
    if (s.includes("active") || s.includes("success")) return "#31a66a";
    if (s.includes("pending")) return "#c47a2a";
    if (s.includes("reject") || s.includes("fail")) return "#e44862";
    return "rgba(23,23,23,0.65)";
  }};
`;

const RemarksRow = styled.div`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid rgba(23, 23, 23, 0.06);
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const RemarksText = styled.span`
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.65);
  line-height: 1.4;
`;

const TransactionsSection = styled.div`
  width: 100%;
`;

const FilterBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
`;

const FilterPill = styled.button<{ $active: boolean }>`
  padding: 6px 14px;
  border-radius: 999px;
  border: 1px solid ${(p) => (p.$active ? "#c94daa" : "rgba(23, 23, 23, 0.1)")};
  background: ${(p) =>
    p.$active
      ? "linear-gradient(135deg, rgba(255, 201, 81, 0.15) 0%, rgba(206, 86, 176, 0.12) 100%)"
      : "#ffffff"};
  color: ${(p) => (p.$active ? "#c94daa" : "rgba(23, 23, 23, 0.65)")};
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: #c94daa;
    color: #c94daa;
  }
`;

